import nacl from 'tweetnacl';
import { encodeBase64, decodeBase64 } from 'tweetnacl-util';

import type { VaultItemPayload, UserKeypair } from '../types';

import { bytesToBase64, base64ToBytes } from './kdf';

/**
 * Generate a random encryption key (for item keys)
 */
export function generateRandomKey(): Uint8Array {
  return nacl.randomBytes(32);
}

/**
 * Generate a random nonce
 */
export function generateNonce(): Uint8Array {
  return nacl.randomBytes(24);
}

/**
 * Encrypt data using XChaCha20-Poly1305 (via nacl.secretbox)
 */
export function encrypt(
  plaintext: Uint8Array,
  key: Uint8Array
): { ciphertext: Uint8Array; nonce: Uint8Array } {
  const nonce = generateNonce();
  const ciphertext = nacl.secretbox(plaintext, nonce, key);
  return { ciphertext, nonce };
}

/**
 * Decrypt data using XChaCha20-Poly1305 (via nacl.secretbox)
 */
export function decrypt(
  ciphertext: Uint8Array,
  nonce: Uint8Array,
  key: Uint8Array
): Uint8Array | null {
  return nacl.secretbox.open(ciphertext, nonce, key);
}

/**
 * Encrypt a string payload
 */
export function encryptPayload(
  payload: VaultItemPayload,
  key: Uint8Array
): { encryptedPayload: string; payloadNonce: string } {
  const plaintext = new TextEncoder().encode(JSON.stringify(payload));
  const { ciphertext, nonce } = encrypt(plaintext, key);

  return {
    encryptedPayload: bytesToBase64(ciphertext),
    payloadNonce: bytesToBase64(nonce),
  };
}

/**
 * Decrypt a string payload
 */
export function decryptPayload(
  encryptedPayload: string,
  payloadNonce: string,
  key: Uint8Array
): VaultItemPayload | null {
  const ciphertext = base64ToBytes(encryptedPayload);
  const nonce = base64ToBytes(payloadNonce);

  const plaintext = decrypt(ciphertext, nonce, key);
  if (!plaintext) {
    return null;
  }

  try {
    const json = new TextDecoder().decode(plaintext);
    return JSON.parse(json) as VaultItemPayload;
  } catch {
    return null;
  }
}

/**
 * Encrypt item key with vault key
 */
export function encryptItemKey(
  itemKey: Uint8Array,
  vaultKey: Uint8Array
): { itemKeyEncrypted: string; itemKeyNonce: string } {
  const { ciphertext, nonce } = encrypt(itemKey, vaultKey);

  return {
    itemKeyEncrypted: bytesToBase64(ciphertext),
    itemKeyNonce: bytesToBase64(nonce),
  };
}

/**
 * Decrypt item key with vault key
 */
export function decryptItemKey(
  itemKeyEncrypted: string,
  itemKeyNonce: string,
  vaultKey: Uint8Array
): Uint8Array | null {
  const ciphertext = base64ToBytes(itemKeyEncrypted);
  const nonce = base64ToBytes(itemKeyNonce);

  return decrypt(ciphertext, nonce, vaultKey);
}

/**
 * Generate X25519 keypair for sharing
 */
export function generateKeypair(): UserKeypair {
  const keypair = nacl.box.keyPair();
  return {
    publicKey: keypair.publicKey,
    privateKey: keypair.secretKey,
  };
}

/**
 * Encrypt item key for a recipient using their public key (sealed box)
 */
export function encryptItemKeyForRecipient(
  itemKey: Uint8Array,
  recipientPublicKey: Uint8Array,
  senderPrivateKey: Uint8Array
): { encryptedKey: string; nonce: string } {
  const nonce = nacl.randomBytes(24);
  const encrypted = nacl.box(itemKey, nonce, recipientPublicKey, senderPrivateKey);

  return {
    encryptedKey: bytesToBase64(encrypted),
    nonce: bytesToBase64(nonce),
  };
}

/**
 * Decrypt item key received from sharing
 */
export function decryptItemKeyFromSharing(
  encryptedKey: string,
  nonce: string,
  senderPublicKey: Uint8Array,
  recipientPrivateKey: Uint8Array
): Uint8Array | null {
  const ciphertext = base64ToBytes(encryptedKey);
  const nonceBytes = base64ToBytes(nonce);

  return nacl.box.open(ciphertext, nonceBytes, senderPublicKey, recipientPrivateKey);
}

/**
 * Encrypt private key with vault key (for storage)
 */
export function encryptPrivateKey(
  privateKey: Uint8Array,
  vaultKey: Uint8Array
): { encryptedPrivateKey: string; privateKeyNonce: string } {
  const { ciphertext, nonce } = encrypt(privateKey, vaultKey);

  return {
    encryptedPrivateKey: bytesToBase64(ciphertext),
    privateKeyNonce: bytesToBase64(nonce),
  };
}

/**
 * Decrypt private key with vault key
 */
export function decryptPrivateKey(
  encryptedPrivateKey: string,
  privateKeyNonce: string,
  vaultKey: Uint8Array
): Uint8Array | null {
  const ciphertext = base64ToBytes(encryptedPrivateKey);
  const nonce = base64ToBytes(privateKeyNonce);

  return decrypt(ciphertext, nonce, vaultKey);
}

/**
 * Encode public key for transmission
 */
export function encodePublicKey(publicKey: Uint8Array): string {
  return encodeBase64(publicKey);
}

/**
 * Decode public key from transmission
 */
export function decodePublicKey(publicKeyBase64: string): Uint8Array {
  return decodeBase64(publicKeyBase64);
}

